/**
 * Clearing-bearing editor. The fixed end stays pinned to the charted object; set
 * the bearing here or by dragging the free end on the chart, pick which side is
 * safe (the tinted danger side on the chart updates as you flip), name it, switch its
 * guard on or off, and file it under a pilotage plan. Read-only in Live, where
 * the clearing is guarding rather than being edited.
 */
import { useEffect, useState } from 'react';
import { useChartPlotterStore } from '@/store';
import { useShellStore } from '@/store/shell';
import { usePilotageStore } from '@/store/pilotage';
import { clearingDisplayName, clearingSide, isClearingTripped } from '@/pilotage';
import { formatBearing, formatPosition } from '@/nav';
import { Sheet } from './Sheet';

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="acp-kv">
      <span className="k">{k}</span>
      <span className="v">{v}</span>
    </div>
  );
}

export function ClearingSheet() {
  const activeId = usePilotageStore((s) => s.activeClearingId);
  const clearings = usePilotageStore((s) => s.clearings);
  const plans = usePilotageStore((s) => s.plans);
  const updateClearing = usePilotageStore((s) => s.updateClearing);
  const removeClearing = usePilotageStore((s) => s.removeClearing);
  const setActiveClearing = usePilotageStore((s) => s.setActiveClearing);
  const assignClearingToPlan = usePilotageStore((s) => s.assignClearingToPlan);
  const addPlan = usePilotageStore((s) => s.addPlan);

  const mode = useShellStore((s) => s.mode);
  const closeSheet = useShellStore((s) => s.closeSheet);
  const showToast = useShellStore((s) => s.showToast);
  const boat = useChartPlotterStore((s) => s.ownship.position);

  const cb = clearings.find((c) => c.id === activeId);

  const [name, setName] = useState('');
  const [bearing, setBearing] = useState('');
  // Seed on selection only, so typing isn't clobbered. The chart label follows
  // a drag live regardless; this field is for typing a value in.
  useEffect(() => {
    if (!cb) return;
    setName(cb.name ?? '');
    setBearing(String(Math.round(cb.bearingDeg)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  if (!cb) return <Sheet name="clearing" title="Clearing bearing" />;

  const readOnly = mode === 'Live';
  const tripped = isClearingTripped(cb, boat);
  const bearingText = formatBearing(cb.bearingDeg);

  if (readOnly) {
    return (
      <Sheet
        name="clearing"
        title="Clearing bearing"
        sub="Read-only while we're in Live. It's guarding: cross to the unsafe side and the alarm fires. Switch to Plotting to change it, or delete it here."
      >
        <Row k="Name" v={clearingDisplayName(cb)} />
        <Row k="On object" v={cb.anchorLabel || 'Charted object'} />
        <Row k="Object position" v={formatPosition(cb.anchor)} />
        <Row k="Clearing bearing" v={bearingText} />
        <Row k="Guard" v={cb.alarm ? 'On' : 'Off'} />
        <Row k="Status" v={tripped ? 'On the UNSAFE side' : boat ? 'On the safe side' : 'No fix yet'} />
        {/* Deleting is removal, not editing a live guard, so we allow it even in
            Live: a clearing dropped by mistake can always be cleared away. */}
        <div className="acp-btn-row">
          <button
            type="button"
            className="acp-btn"
            onClick={() => {
              removeClearing(cb.id);
              setActiveClearing(null);
              closeSheet();
              showToast('Clearing bearing deleted');
            }}
          >
            Delete
          </button>
          <button
            type="button"
            className="acp-btn primary"
            onClick={() => {
              setActiveClearing(null);
              closeSheet();
            }}
          >
            Done
          </button>
        </div>
      </Sheet>
    );
  }

  function commitName(v: string) {
    setName(v);
    if (cb) updateClearing(cb.id, { name: v });
  }
  function commitBearing(v: string) {
    setBearing(v);
    const n = parseFloat(v);
    if (cb && Number.isFinite(n)) updateClearing(cb.id, { bearingDeg: ((n % 360) + 360) % 360 });
  }
  function putOnMySide() {
    if (cb && boat) {
      const side = clearingSide(cb, boat);
      if (side !== 'on') updateClearing(cb.id, { safeSide: side });
    }
  }
  function del() {
    if (!cb) return;
    removeClearing(cb.id);
    setActiveClearing(null);
    closeSheet();
    showToast('Clearing bearing deleted');
  }

  return (
    <Sheet
      name="clearing"
      title="Clearing bearing"
      sub="Pinned to the object; drag the free end on the chart to set the bearing, or type it. The bearing is the object's compass bearing from us."
    >
      <Row k="On object" v={cb.anchorLabel || 'Charted object'} />
      <label className="acp-fieldlabel">Name</label>
      <input
        className="acp-text-input"
        value={name}
        onChange={(e) => commitName(e.target.value)}
        placeholder={clearingDisplayName(cb)}
      />
      <label className="acp-fieldlabel">Clearing bearing (°T, object from us)</label>
      <input
        className="acp-text-input"
        value={bearing}
        onChange={(e) => commitBearing(e.target.value)}
        placeholder="045"
        inputMode="decimal"
        aria-label="Clearing bearing in degrees true"
      />
      <label className="acp-fieldlabel">Safe side</label>
      <div className="acp-seg">
        <button
          type="button"
          className={cb.safeSide === 'left' ? 'on' : ''}
          aria-pressed={cb.safeSide === 'left'}
          onClick={() => updateClearing(cb.id, { safeSide: 'left' })}
        >
          Left
        </button>
        <button
          type="button"
          className={cb.safeSide === 'right' ? 'on' : ''}
          aria-pressed={cb.safeSide === 'right'}
          onClick={() => updateClearing(cb.id, { safeSide: 'right' })}
        >
          Right
        </button>
      </div>
      {boat && (
        <div className="acp-btn-row">
          <button type="button" className="acp-btn full" onClick={putOnMySide}>
            Safe = my side
          </button>
        </div>
      )}
      <p className="acp-note">
        The highlighted side above is the safe side; the soft-tinted, ticked side on the chart is the
        danger side to avoid.{tripped ? ' We are on the unsafe side right now.' : ''}
      </p>
      <label className="acp-fieldlabel">Guard (cross alarm)</label>
      <div className="acp-seg">
        <button type="button" className={!cb.alarm ? 'on' : ''} onClick={() => updateClearing(cb.id, { alarm: false })}>
          Off
        </button>
        <button type="button" className={cb.alarm ? 'on' : ''} onClick={() => updateClearing(cb.id, { alarm: true })}>
          On
        </button>
      </div>
      <label className="acp-fieldlabel">Pilotage plan</label>
      <div className="acp-seg" style={{ flexWrap: 'wrap' }}>
        <button type="button" className={cb.planId === null ? 'on' : ''} onClick={() => assignClearingToPlan(cb.id, null)}>
          Loose
        </button>
        {plans.map((p) => (
          <button key={p.id} type="button" className={cb.planId === p.id ? 'on' : ''} onClick={() => assignClearingToPlan(cb.id, p.id)}>
            {p.name}
          </button>
        ))}
        <button
          type="button"
          onClick={() => {
            const id = addPlan();
            assignClearingToPlan(cb.id, id);
            showToast('Added to a new plan');
          }}
        >
          + New plan
        </button>
      </div>
      <div className="acp-btn-row">
        <button type="button" className="acp-btn" onClick={del}>
          Delete
        </button>
        <button
          type="button"
          className="acp-btn primary"
          onClick={() => {
            setActiveClearing(null);
            closeSheet();
          }}
        >
          Done
        </button>
      </div>
    </Sheet>
  );
}
